import { Cubit } from "blac-next";
import type {
  AnchorMeta,
  TourDefinition,
  TourGuideState,
  TourGuideStorage
} from "./types";
import { STEP_TRANSITION_DELAY_MS } from "./constants";

const STORAGE_PREFIX = "tourGuide.seen.";

const IDLE_STATE: TourGuideState = {
  activeTourId: null,
  currentStepIndex: 0,
  steps: [],
  isTransitioning: false
};

const NOOP_STORAGE: TourGuideStorage = {
  getItem: () => null,
  setItem: () => {}
};

export default class TourGuideCubit extends Cubit<TourGuideState> {
  static keepAlive = true;

  private tours = new Map<string, TourDefinition>();
  private anchors = new Map<string, AnchorMeta>();
  private storage: TourGuideStorage = NOOP_STORAGE;

  constructor() {
    super({ ...IDLE_STATE });
  }

  // ── Configuration ─────────────────────────────────────

  setStorage = (storage: TourGuideStorage): void => {
    this.storage = storage;
  };

  // ── Anchor registry ───────────────────────────────────

  isAnchorRegistered = (name: string): boolean => this.anchors.has(name);

  getAnchorMeta = (name: string): AnchorMeta | undefined =>
    this.anchors.get(name);

  registerAnchor = (name: string, meta: AnchorMeta = {}): void => {
    this.anchors.set(name, meta);
    const { activeTourId, steps, currentStepIndex } = this.state;
    if (activeTourId && steps[currentStepIndex]?.anchorName === name) {
      this.patch({});
    }
  };

  unregisterAnchor = (name: string): void => {
    this.anchors.delete(name);
  };

  // ── Tour registry ─────────────────────────────────────

  registerTour = (tour: TourDefinition): void => {
    this.tours.set(tour.id, tour);
  };

  // ── Tour lifecycle ────────────────────────────────────

  startTour = async (tourId: string, force = false): Promise<void> => {
    if (this.state.activeTourId) {
      if (!force) {
        return;
      }
      this.patch({ ...IDLE_STATE });
    }
    if (!force && this.hasSeenTour(tourId)) {
      return;
    }

    const tour = this.tours.get(tourId);
    if (!tour) {
      return;
    }

    const steps = tour.resolveSteps();
    if (steps.length === 0) {
      return;
    }

    this.patch({
      activeTourId: tourId,
      currentStepIndex: 0,
      steps,
      isTransitioning: true
    });

    await this.runHook(steps[0].runBefore);
    await this.delay(STEP_TRANSITION_DELAY_MS);
    this.patch({ isTransitioning: false });
  };

  next = async (): Promise<void> => {
    const { steps, currentStepIndex } = this.state;

    if (currentStepIndex >= steps.length - 1) {
      await this.endTour(true);
      return;
    }

    this.patch({ isTransitioning: true });
    await this.waitForFrame();

    await this.runHook(steps[currentStepIndex]?.runAfter);

    const nextIndex = currentStepIndex + 1;
    await this.runHook(steps[nextIndex]?.runBefore);
    await this.delay(STEP_TRANSITION_DELAY_MS);

    this.patch({
      currentStepIndex: nextIndex,
      isTransitioning: false
    });
  };

  dismiss = async (): Promise<void> => {
    await this.endTour(false);
  };

  completeTour = async (): Promise<void> => {
    await this.endTour(true);
  };

  // ── Private ───────────────────────────────────────────

  private async endTour(markSeen: boolean): Promise<void> {
    const { activeTourId, steps, currentStepIndex } = this.state;
    this.patch({ isTransitioning: true });
    await this.waitForFrame();
    await this.runHook(steps[currentStepIndex]?.runAfter);

    if (markSeen && activeTourId) {
      this.markTourSeen(activeTourId);
    }

    this.patch({ ...IDLE_STATE });
  }

  private hasSeenTour(tourId: string): boolean {
    return this.storage.getItem(`${STORAGE_PREFIX}${tourId}`) === "true";
  }

  private markTourSeen(tourId: string): void {
    this.storage.setItem(`${STORAGE_PREFIX}${tourId}`, "true");
  }

  private waitForFrame(): Promise<void> {
    return new Promise((resolve) => requestAnimationFrame(() => resolve()));
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private async runHook(
    hook: (() => void | Promise<void>) | undefined
  ): Promise<void> {
    if (hook) {
      await hook();
    }
  }
}
